import axios, {
  AxiosError,
  InternalAxiosRequestConfig,
  AxiosResponse,
} from "axios";
import {
  useAdminAuthStore,
  waitForRefreshInFlight,
} from "@/stores/adminAuthStore";
import { isErrorResponse, type ErrorResponse } from "@/types/api-response";

/**
 * Get API Base URL
 * Priority:
 * 1. Runtime config from window.APP_CONFIG (Kubernetes ConfigMap in production)
 * 2. Environment variable VITE_API_BASE_URL (build-time configuration)
 * 3. Empty string for relative URLs (development with Vite proxy)
 */
const getApiBaseUrl = (): string => {
  // Production: Use runtime config from Kubernetes ConfigMap
  if (window.APP_CONFIG?.apiUrl) {
    return window.APP_CONFIG.apiUrl;
  }

  // Build-time: Use environment variable if set
  const envUrl = import.meta.env.VITE_API_BASE_URL;
  if (envUrl) {
    return envUrl;
  }

  // Development: Use relative URL (Vite proxy handles routing to localhost:5101)
  return "";
};

export const API_BASE_URL = getApiBaseUrl();

/**
 * Request timeout in milliseconds.
 *
 * The instance originally had none, so axios waited forever: a request the server never answers
 * left the screen spinning with no error and no way to retry.
 *
 * 90s, matching the client app, and deliberately longer than the backend's own WiService budget
 * of 60s (`Math.Max(WiService:Resilience:TimeoutSeconds, WiService:TimeoutSeconds)`, set by
 * `config.wiService.timeoutSeconds` in every cluster values file). The earlier 30s aborted calls
 * the backend was still allowed to be making, turning a slow-but-successful provider request into
 * a timeout on screen. The server should be the side that gives up first, because its error
 * carries a code; the browser is only the backstop against waiting forever.
 */
export const API_TIMEOUT = 90000;

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  timeout: API_TIMEOUT,
  headers: {
    "Content-Type": "application/json",
  },
});

// ============================================================================
// Custom API Error Class for structured error handling
// ============================================================================

export class APIError extends Error {
  constructor(
    public code: string,
    message: string,
    public details?: unknown,
    public statusCode?: number,
  ) {
    super(message);
    this.name = "APIError";
    Object.setPrototypeOf(this, APIError.prototype);
  }

  /**
   * Check if error is a specific code
   */
  isCode(code: string): boolean {
    return this.code === code;
  }

  /**
   * Check if error is authentication related
   */
  isAuthError(): boolean {
    return this.code === "UNAUTHORIZED" || this.statusCode === 401;
  }

  /**
   * Check if error is validation related
   */
  isValidationError(): boolean {
    return this.code === "VALIDATION_ERROR" || this.statusCode === 422;
  }

  /**
   * Check if error is not found
   */
  isNotFoundError(): boolean {
    return this.code === "NOT_FOUND" || this.statusCode === 404;
  }

  /**
   * Check if error is forbidden
   */
  isForbiddenError(): boolean {
    return this.code === "FORBIDDEN" || this.statusCode === 403;
  }

  /**
   * True when the request never got an answer — the two codes the response interceptor raises
   * when there is no HTTP status to report at all.
   *
   * Everything else here carries a status the server chose. An ISP that blackholes traffic
   * (FPT, 2026-09-22) lands in this branch: no status, nothing the browser can act on, just
   * silence until API_TIMEOUT. See the retry rule in App.tsx for why it must not be retried.
   */
  isTransportFailure(): boolean {
    return this.code === "TIMEOUT" || this.code === "NETWORK_ERROR";
  }
}

// ============================================================================
// Global refresh lock to prevent race conditions
// ============================================================================

/**
 * The refresh endpoint's own path. A 401 from it means the refresh token is dead too, which is
 * the one 401 the refresh flow must not try to handle — see the guard in the response interceptor.
 */
const REFRESH_ENDPOINT = "/api/v1/admin/auth/refresh";

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (value?: unknown) => void;
  reject: (reason?: unknown) => void;
}> = [];

const processQueue = (error: Error | null, token: string | null = null) => {
  failedQueue.forEach((prom) => {
    if (error) {
      prom.reject(error);
    } else {
      prom.resolve(token);
    }
  });

  failedQueue = [];
};

// ============================================================================
// Request interceptor - add auth token
// ============================================================================

apiClient.interceptors.request.use(
  async (config) => {
    // A refresh already running is waited for, so the request leaves with the token it produces.
    // `main.tsx` starts one on boot when the stored token has lapsed, and the first screen's
    // queries used to race it: each was answered 401 and only then retried — six failed requests
    // in the console on every visit after the token expired. The refresh call itself is a bare
    // `axios.post`, so it never comes through here and cannot wait on itself.
    await waitForRefreshInFlight();

    const token = useAdminAuthStore.getState().token;
    if (token) {
      config.headers["Authorization"] = `Bearer ${token}`;
    }
    return config;
  },
  (error) => Promise.reject(error),
);

// ============================================================================
// Response interceptor - handle StandardResponse format + 401 refresh
// ============================================================================

apiClient.interceptors.response.use(
  (response: AxiosResponse) => {
    // Check if response is an error response (StandardResponse with success: false)
    if (isErrorResponse(response.data)) {
      const errorData = response.data as ErrorResponse;
      throw new APIError(
        errorData.error.code,
        errorData.error.message,
        errorData.error.details,
        response.status,
      );
    }

    // Return response as-is for success responses
    // Service layer will extract data from response.data
    return response;
  },
  async (error: AxiosError) => {
    const originalRequest = error.config as InternalAxiosRequestConfig & {
      _retry?: boolean;
    };

    // A cancelled request is not a failure: React Query aborts in-flight queries on
    // unmount and refetch, and the browser aborts everything on navigation or reload.
    // Passing it straight back keeps it out of the branches below, which would
    // otherwise dress it up as a NETWORK_ERROR the UI reports as a dead connection.
    // A real timeout is NOT a cancellation (axios reports ECONNABORTED, `isCancel`
    // is false), so it still reaches the timeout branch.
    if (axios.isCancel(error)) {
      return Promise.reject(error);
    }

    // A 401 from the refresh endpoint itself is not something a refresh can fix — the session
    // is dead. It must never enter the branch below: while a refresh is in flight that branch
    // parks the request in `failedQueue`, so the refresh response ended up queued behind the
    // very refresh it was answering. `processQueue` then never ran, `isRefreshing` stayed true,
    // and logout/redirect never fired — the console spun forever on 401s it could not recover
    // from. `adminAuthService.refreshToken` also bypasses this instance entirely; this guard is
    // the invariant, stated where the deadlock was.
    const isRefreshCall =
      originalRequest?.url?.includes(REFRESH_ENDPOINT) ?? false;

    // Handle 401 Unauthorized - attempt token refresh
    if (
      error.response?.status === 401 &&
      originalRequest &&
      !originalRequest._retry &&
      !isRefreshCall
    ) {
      if (isRefreshing) {
        // Queue this request
        return new Promise((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        })
          .then((token) => {
            originalRequest.headers!["Authorization"] = `Bearer ${token}`;
            return apiClient(originalRequest);
          })
          .catch((err) => {
            return Promise.reject(err);
          });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        await useAdminAuthStore.getState().refreshAccessToken();
        const newToken = useAdminAuthStore.getState().token;
        if (!newToken) {
          // Refresh reported success but left no token. Retrying with `Bearer null` would only
          // earn another 401; treat it as a failed refresh so the session is cleared instead.
          throw new Error("Token refresh completed without an access token");
        }
        processQueue(null, newToken);

        originalRequest.headers!["Authorization"] = `Bearer ${newToken}`;
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError as Error, null);
        // `refreshAccessToken` ends the session itself when the server refuses the token. A 5xx
        // or a dropped connection fails this request only — signing out over it would sign the
        // operator out of every tab for a blip that says nothing about the token.
        const { isAuthenticated, token } = useAdminAuthStore.getState();
        if (!isAuthenticated || !token) {
          useAdminAuthStore.getState().logout();
          window.location.href = "/login";
        }
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    // Handle StandardResponse error format from server
    if (error.response?.data && isErrorResponse(error.response.data)) {
      const errorData = error.response.data as ErrorResponse;
      return Promise.reject(
        new APIError(
          errorData.error.code,
          errorData.error.message,
          errorData.error.details,
          error.response.status,
        ),
      );
    }

    // Timeout - the request reached the server, which just took too long. Reporting
    // it as a connectivity problem sends the admin chasing their own network.
    if (error.code === "ECONNABORTED" || error.code === "ETIMEDOUT") {
      return Promise.reject(
        new APIError(
          "TIMEOUT",
          `The server did not respond within ${API_TIMEOUT / 1000} seconds. Please try again.`,
          undefined,
          0,
        ),
      );
    }

    // Network errors or other issues. Axios sets `code` for these; the message match
    // is kept as a fallback for adapters that do not.
    if (error.code === "ERR_NETWORK" || error.message === "Network Error") {
      return Promise.reject(
        new APIError(
          "NETWORK_ERROR",
          "Network connection failed. Please check your internet connection.",
          undefined,
          0,
        ),
      );
    }

    // Generic error handling
    return Promise.reject(error);
  },
);
